import { useState, useEffect } from 'react';
import { env } from '@/config/env';
import { normalizeNavItems } from '@/utils/route-utils';
import { authService } from '@/auth';
import type { RemoteNavItem, RemoteMountOptionsBase } from '@platform-ui/remote-types';

/**
 * Shape of a federated module that exports nav items.
 */
type NavItemsModule = {
    getRemoteNavItems?: (options: RemoteMountOptionsBase) => Promise<RemoteNavItem[]> | RemoteNavItem[];
    default?: RemoteNavItem[];
};

/**
 * Extracts nav items from a federated module result.
 */
async function extractNavItems(moduleResult: unknown, mountOptions: RemoteMountOptionsBase): Promise<RemoteNavItem[]> {
    if (Array.isArray(moduleResult)) {
        return moduleResult;
    }

    const mod = moduleResult as NavItemsModule;

    if (typeof mod.getRemoteNavItems === 'function') {
        return await mod.getRemoteNavItems(mountOptions);
    }

    if (mod.default) {
        return mod.default;
    }

    return [];
}

/**
 * Hook to load all nav items from all configured apps on mount.
 * Supports both inline navItems from config and remote remoteNavItems.
 *
 * @returns Map of basePath -> normalized nav items
 */
export function useAllNavItems() {
    const [navItems, setNavItems] = useState<Map<string, RemoteNavItem[]>>(new Map());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadAllNavItems = async () => {
            const navItemsMap = new Map<string, RemoteNavItem[]>();
            const appsWithNavItems = env.apps.filter(config => config.navItems || config.importRemoteNavItems);

            const loadPromises = appsWithNavItems.map(async config => {
                const basePath = config.basePath || '';

                try {
                    let raw: RemoteNavItem[];

                    if (config.navItems) {
                        // Use static nav items from config
                        raw = config.navItems;
                    } else if (config.importRemoteNavItems) {
                        // Load from remote module
                        const moduleResult = await config.importRemoteNavItems();

                        const mountOptions: RemoteMountOptionsBase = {
                            basePath: config.basePath,
                            federatedAuth: authService.createFederatedAuth(),
                            location: window.location.href
                        };

                        raw = await extractNavItems(moduleResult, mountOptions);
                    } else {
                        raw = [];
                    }

                    const normalized = normalizeNavItems(raw, basePath || '/');
                    navItemsMap.set(basePath, normalized);
                } catch (err) {
                    console.error(`Failed to load nav items for ${config.appName}:`, err);
                    navItemsMap.set(basePath, []);
                }
            });

            await Promise.all(loadPromises);

            if (!cancelled) {
                setNavItems(navItemsMap);
                setIsLoading(false);
            }
        };

        loadAllNavItems();

        return () => {
            cancelled = true;
        };
    }, []);

    return { navItems, isLoading };
}

/**
 * Get nav items for a specific basePath from the loaded map.
 */
export function getNavItemsForBasePath(navItemsMap: Map<string, RemoteNavItem[]>, basePath: string | undefined): RemoteNavItem[] | null {
    if (basePath === undefined) return null;
    return navItemsMap.get(basePath) || null;
}

/**
 * Get home nav items from the loaded map.
 * Supports both '' and '/' as home basePath.
 */
export function getHomeNavItems(navItemsMap: Map<string, RemoteNavItem[]>): RemoteNavItem[] | null {
    return navItemsMap.get('') || navItemsMap.get('/') || null;
}
